// -----------------------------------------------------------------------------
// UsersManagement.jsx – version “full‑copy”, rôles & abonnement modernisés
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllUsers,
  fetchAssignedUsers,
  removeUser,
  updateUser,
  fetchUserById,
  toggleBlockUser,
} from "../../redux/usersSlice";

import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Modal,
  Box,
  Stack,
  Alert,
  Chip,
  Tooltip,
  useTheme,
} from "@mui/material";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Add as AddIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as AdminIcon,
  Groups as UserIcon,
  Star as StarIcon,
  PersonOutline as PersonOutlineIcon,
} from "@mui/icons-material";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import ModifyUserModal from "./ModifyUserModal";
import SignUp from "../Accueil/SignUp";
import DeleteModal from "../Common/DeleteModal";
import ConfirmationActionModal from "../Common/ConfirmationActionModal";
import CustomLoader from "../Common/CustomLoader";

import "./styles/UsersManagement.css";

/* -------------------------------------------------------------------------- */
/*  Helpers & constantes                                                      */
/* -------------------------------------------------------------------------- */

// Mappage rôles → label, couleur, icône
const ROLE_CONFIG = {
  ROLE_SUPER_ADMIN: {
    label: "Super Admin",
    bg: "#36bcff",
    icon: <ShieldIcon />,
  },
  ROLE_ADMIN: {
    label: "Admin",
    bg: "#5dafee",
    icon: <AdminIcon />,
  },
  ROLE_COLLABORATOR: {
    label: "Collaborator",
    bg: "#9ebac8",
    icon: <UserIcon />,
  },
  default: {
    label: "Freelance",
    bg: "#bcbcbc",
    icon: <UserIcon />,
  },
};

// Couleurs puces abonnement
const PREMIUM_BG = "#ffd700";
const STANDARD_BG = "#b0c4de";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#fff",
  boxShadow: 24,
  borderRadius: 3,
};

const PER = 5;

/* -------------------------------------------------------------------------- */
/*  Composant                                                                 */
/* -------------------------------------------------------------------------- */

export default function UsersManagement() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    users,
    assignedUsers,
    loading,
    selectedUser,
    error,
    currentUser,
  } = useSelector((s) => s.users);

  /* ----------------------------- state local ----------------------------- */
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openSignUpModal, setOpenSignUpModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isCurrentlyBlocked, setIsCurrentlyBlocked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState(null);

  /* ----------------------------- chargement ----------------------------- */
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.roles.includes("ROLE_SUPER_ADMIN"))
      dispatch(fetchAllUsers());
    else if (currentUser.roles.includes("ROLE_ADMIN"))
      dispatch(fetchAssignedUsers());
  }, [dispatch, currentUser]);

  /* ------------------- pagination / slice --------------------- */
  const data = currentUser?.roles?.includes("ROLE_ADMIN")
    ? assignedUsers
    : users;
  const totalPages = Math.ceil(data.length / PER);
  const pageSlice = data.slice((currentPage - 1) * PER, currentPage * PER);

  /* --------------------------- handlers UI ------------------------------ */
  const toggleRow = (id) =>
    setExpandedRowId((prev) => (prev === id ? null : id));

  const handleOpenEditModal = async (u) => {
    await dispatch(fetchUserById(u.id));
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => setOpenEditModal(false);

  const handleOpenSignUpModal = () => setOpenSignUpModal(true);
  const handleCloseSignUpModal = () => {
    setOpenSignUpModal(false);
    dispatch(fetchAllUsers());
  };

  /* ---------- suppression ---------- */
  const openDeleteModalFn = (id) => {
    setDeleteUserId(id);
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteUserId(null);
  };
  const confirmDelete = async () => {
    if (deleteUserId) {
      await dispatch(removeUser(deleteUserId));
      setSuccessMessage("Suppression effectuée");
      setTimeout(() => setSuccessMessage(""), 3000);
      closeDeleteModal();
    }
  };

  /* ---------- block / unblock ---------- */
  const openBlockModalFn = (id, blocked) => {
    setCurrentUserId(id);
    setIsCurrentlyBlocked(blocked);
    setOpenBlockModal(true);
  };
  const closeBlockModal = () => setOpenBlockModal(false);
  const confirmBlockUser = () => {
    dispatch(
      toggleBlockUser({ id: currentUserId, isBlocked: !isCurrentlyBlocked })
    ).then(() => dispatch(fetchUserById(currentUserId)));
    setOpenBlockModal(false);
  };
  const handleUnblockDirect = (id) =>
    dispatch(toggleBlockUser({ id, isBlocked: false })).then(() =>
      dispatch(fetchUserById(id))
    );

  // Helper to extract a user-friendly error message
  const getErrorMessage = (error) => {
    if (!error) return "";
    if (typeof error === "string") return error;
    if (error.error) return error.error;
    if (error.messages && typeof error.messages.value === "string") return error.messages.value;
    if (error.messages && Array.isArray(error.messages.value)) return error.messages.value.join(", ");
    return "L'adresse email ou le nom d'utilisateur existe déjà";
  };

  /* -------------------------- render (loading) -------------------------- */
  if (loading) {
    return (
      <Container className="loading-screen">
        <CustomLoader />
      </Container>
    );
  }

  /* -------------------------------- render ------------------------------ */
  return (
    <Container className="users-container">
      <Typography
        variant="h4"
        className="userstitle"
        sx={{
          mb: 4,
          fontWeight: "bold",
          color: "#2F4E8B",
          textAlign: "center",
        }}
      >
        Gestion des utilisateurs
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* bouton Ajouter aligné à droite */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenSignUpModal}
          sx={{
            textTransform: "none",
            bgcolor: "#2F4E8B",
            "&:hover": { bgcolor: "#4a6bad" },
            fontWeight: "bold",
            py: 1,
            px: 3,
            borderRadius: "50px",
            color: "#fff",
          }}
        >
          Ajouter
        </Button>
      </Box>

      {/* tableau ----------------------------------------------------------- */}
      <TableContainer
        component={Paper}
        className="table-wrapper"
        sx={{
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#2F4E8B", // Main blue color
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#1a2d4d", // Slightly darker blue on hover
          },
          "&::-webkit-scrollbar-thumb:horizontal": {
            backgroundColor: "#2F4E8B",
          },
          "&::-webkit-scrollbar-thumb:horizontal:hover": {
            backgroundColor: "#1a2d4d",
          },
          scrollbarColor: "#2F4E8B #e6e8f0", // For Firefox
          scrollbarWidth: "thin",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow className="table-head-row">
              <TableCell width={44} />
              <TableCell
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Nom d’utilisateur
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Rôles
              </TableCell>

              <TableCell
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Abonnement
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Entreprise
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageSlice.map((u) => {
              const expanded = expandedRowId === u.id;
              return (
                <React.Fragment key={u.id}>
                  <TableRow
                    hover
                    className={`table-body-row ${expanded ? "expanded" : ""}`}
                    sx={{
                      "& .MuiTableCell-root": {
                        fontSize: "0.9rem",
                        color: "#4a4a4a",
                      },
                    }}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(u.id)}
                        className={`arrow-btn ${expanded ? "open" : ""}`}
                      >
                        {expanded ? (
                          <ArrowDownIcon fontSize="inherit" />
                        ) : (
                          <ArrowRightIcon fontSize="inherit" />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>

                    {/* ----------- Rôles (puces Chip) ----------- */}
                    <TableCell>
                      {u.roles?.length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {u.roles.map((r) => {
                            const cfg = ROLE_CONFIG[r] ?? ROLE_CONFIG.default;
                            return (
                              <Tooltip key={r} title={r} arrow>
                                <Chip
                                  size="small"
                                  label={cfg.label}
                                  icon={cfg.icon}
                                  sx={{
                                    bgcolor: cfg.bg,
                                    color: "#fff",
                                    fontWeight: 400,
                                    width: 100, // fixed width for all roles
                                    justifyContent: "center", // center icon + label
                                    "& .MuiChip-label": {
                                      width: "100%",
                                      textAlign: "center", // center the text
                                      padding: 0,
                                    },
                                    "& .MuiChip-icon": {
                                      color: "#fff",
                                      marginLeft: 0,
                                    },
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>

                    {/* ----------- Abonnement (puce Chip) ------- */}
                    <TableCell>
                      {u.isPremium ? (
                        <Tooltip title="Premium" arrow>
                          <Chip
                            size="small"
                            label="Premium"
                            icon={<StarIcon />}
                            sx={{
                              bgcolor: PREMIUM_BG,
                              color: "#fff",
                              fontWeight: 500,
                              "& .MuiChip-icon": { color: "#fff" },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Standard" arrow>
                          <Chip
                            size="small"
                            label="Standard"
                            icon={<PersonOutlineIcon />}
                            sx={{
                              bgcolor: STANDARD_BG,
                              color: "#fff",
                              fontWeight: 500,
                              "& .MuiChip-icon": { color: "#fff" },
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>{u.company?.fullName || "N/A"}</TableCell>

                    {/* ----------- Actions ---------------------- */}
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() => openBlockModalFn(u.id, u.isBlocked)}
                          className="action-icon"
                        >
                          {u.isBlocked ? (
                            <LockOpenIcon fontSize="inherit" />
                          ) : (
                            <LockIcon fontSize="inherit" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditModal(u)}
                          className="action-icon"
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => openDeleteModalFn(u.id)}
                          className="action-icon"
                        >
                          <DeleteIcon
                            fontSize="inherit"
                            sx={{ color: theme.palette.error.main }}
                          />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* ----------- Ligne détails étendue ----------- */}
                  {expanded && (
                    <TableRow className="details-row">
                      <TableCell colSpan={8}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={4}
                        >
                          <Box flex={1}>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#9ca3af" }}
                            >
                              Adresse
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              {u.address || "—"}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#9ca3af" }}
                            >
                              Nom Prénom
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              {u.fullName || "—"}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#9ca3af" }}
                            >
                              Anniversaire
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              {u.birthDate
                                ? new Date(u.birthDate).toLocaleDateString()
                                : "—"}
                            </Typography>
                          </Box>

                          <Box flex={1}>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#9ca3af" }}
                            >
                              Droits
                            </Typography>
                            <Typography sx={{ mb: 1 }}>
                              {u.rules || "—"}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#9ca3af" }}
                            >
                              Téléphone
                            </Typography>
                            <Box sx={{ mb: 1, maxWidth: 240 }}>
                              <PhoneInput
                                country="tn"
                                value={u.phone}
                                disabled
                                inputStyle={{ width: "100%" }}
                              />
                            </Box>

                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#9ca3af" }}
                            >
                              Statut
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography>
                                {u.isBlocked ? "Bloqué" : "Actif"}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  u.isBlocked
                                    ? handleUnblockDirect(u.id)
                                    : openBlockModalFn(u.id, false)
                                }
                              >
                                {u.isBlocked ? (
                                  <LockOpenIcon
                                    fontSize="inherit"
                                    sx={{ color: "green" }}
                                  />
                                ) : (
                                  <LockIcon
                                    fontSize="inherit"
                                    sx={{ color: "red" }}
                                  />
                                )}
                              </IconButton>
                            </Stack>
                          </Box>
                          <Box>
                            <Button
                              size="small"
                              onClick={() => toggleRow(u.id)}
                              sx={{
                                textTransform: "none",
                                ml: 1,
                                color: "#2F4E8B",
                                fontWeight: "bold",
                              }}
                            >
                              {expandedRowId === u.id
                                ? "Voir moins"
                                : "Voir plus"}
                            </Button>
                          </Box>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* pagination flèches ------------------------------------------------- */}
      {data.length > PER && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={4}
          gap={2}
        >
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ArrowBackIosIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#2F4E8B",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {currentPage}
          </Box>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ArrowForwardIosIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>
        </Box>
      )}

      {/* SIGN‑UP MODAL ------------------------------------------------------ */}
      <Modal open={openSignUpModal} onClose={null}>
        <Box
          sx={{
            position: "absolute",
            top: { xs: 0, sm: "50%" },
            left: { xs: 0, sm: "50%" },
            transform: {
              xs: "none",
              sm: "translate(-50%, -50%)"
            },
            width: { xs: "100vw", sm: "auto", md: "auto" },
            maxWidth: { xs: "100vw", sm: 600, md: 820 },
            minWidth: { xs: "100vw", sm: 0, md: 0 },
            height: { xs: "100vh", sm: "auto" },
            maxHeight: { xs: "100vh", sm: "90vh" },
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor: "transparent", // Transparent background
            boxShadow: "none",      // No shadow
            borderRadius: { xs: 0, sm: 3 },
            border: "none",         // No border
            p: { xs: 0, sm: 0 },    // No extra padding
          }}
        >
          <SignUp handleClose={handleCloseSignUpModal} />
        </Box>
      </Modal>

      {/* EDIT MODAL -------------------------------------------------------- */}
      <Modal open={openEditModal} onClose={null}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "100%", sm: 820 },
            bgcolor: "transparent",
            boxShadow: "none",
          }}
        >
          <ModifyUserModal
            open={openEditModal}
            handleClose={handleCloseEditModal}
            user={selectedUser}
            updateUser={(id, data) =>
              dispatch(updateUser({ id, data })).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                  setSuccessMessage("Modification effectuée");
                  setTimeout(() => setSuccessMessage(""), 3000);
                }
              })
            }
          />
        </Box>
      </Modal>

      {/* BLOCK / UNBLOCK CONFIRMATION -------------------------------------- */}
      <ConfirmationActionModal
        open={openBlockModal}
        onClose={closeBlockModal}
        onConfirm={confirmBlockUser}
        message={
          isCurrentlyBlocked
            ? "Voulez‑vous vraiment débloquer cet utilisateur ?"
            : "Voulez‑vous vraiment bloquer cet utilisateur ?"
        }
      />

      {/* DELETE MODAL ------------------------------------------------------- */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message="Êtes‑vous sûr de vouloir supprimer cet utilisateur ?"
      />
    </Container>
  );
}
